import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import NoResults from "../../../../../components/NoResults/NoResults";
import SearchFilter from "../../../../../components/Filter/SearchFilter";
import useFetch from "../../../../../hooks/fetchMSSQL-hook";

const DEFAULT_REPORT_FILTER = {
  reportProduct: "",
  reportMachine: "",
  reportProductType: "Snack",
  sortBy: "none",
};

const formatPrice = value => `${Number(value || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
const formatDeviation = (value, plus = false) => {
  const amount = Number(value || 0);
  return `${plus && amount > 0 ? "+" : ""}${formatPrice(amount)}`;
};

const searchText = value => String(value ?? "").trim().toLocaleLowerCase("pl");
const sortedUnique = values => [...new Set(values.filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, "pl", { numeric: true, sensitivity: "base" }));

const MACHINE_BADGE_CLASS = " mr-1 mb-1";
const MACHINE_BADGE_STYLE = {
  borderRadius: "10px",
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  textAlign: "left",
  maxWidth: "100%",
  verticalAlign: "middle",
  color: "#003FBE",
  textDecoration: "none",

};

function MachineNames({ machines = [], id }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? machines : machines.slice(0, 3);
  if (!machines.length) return <span className="text-muted">—</span>;

  return (
    <div className="small" style={{ lineHeight: 1.7 }} title={machines.map(m => m.Name).join(", ")}>
      {visible.map((machine, index) => (
        <React.Fragment key={`${id}-${machine.MachineId || index}`}>
          {Number(machine.MachineId) > 0 ? (
            <Link
              to={`/machine/${machine.MachineId}`}
              className={MACHINE_BADGE_CLASS}
              style={MACHINE_BADGE_STYLE}
            >
              {machine.Name}
            </Link>
          ) : (
            <span className={MACHINE_BADGE_CLASS} style={MACHINE_BADGE_STYLE}>
              {machine.Name}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function ProductPriceDeviationsChart() {
  const { fetchMssqlApi } = useFetch();
  const fetchRef = useRef(fetchMssqlApi);
  fetchRef.current = fetchMssqlApi;
  const requestRef = useRef(0);
  const appliedFilterRef = useRef(DEFAULT_REPORT_FILTER);
  const [appliedFilter, setAppliedFilter] = useState(DEFAULT_REPORT_FILTER);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = useCallback((nextFilter = DEFAULT_REPORT_FILTER) => {
    const next = { ...DEFAULT_REPORT_FILTER, ...nextFilter };
    const requestedType = ["Wszystkie", "Kawa", "Snack"].includes(next.reportProductType)
      ? next.reportProductType : "Snack";
    next.reportProductType = requestedType;
    appliedFilterRef.current = next;
    setAppliedFilter(next);

    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    fetchRef.current(
      "stat/product-price-deviations",
      { method: "POST", hideNotification: true, data: { productType: requestedType, sortBy: next.sortBy || "none" } },
      data => {
        if (requestId !== requestRef.current) return;
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      },
      err => {
        if (requestId !== requestRef.current) return;
        console.error("[ProductPriceDeviationsChart]", err);
        setError("Nie udało się pobrać odchyleń cen. Spróbuj ponownie.");
        setRows([]);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => () => { requestRef.current += 1; }, []);

  const reportOptions = useMemo(() => ({
    products: sortedUnique(rows.map(row => {
      const name = String(row.ProductName || "Produkt bez nazwy").trim();
      return row.EAN ? `${name} (${row.EAN})` : name;
    })),
    machines: sortedUnique(rows.flatMap(row =>
      (row.AllMachines || []).map(machine => machine.Name || "")
    )),
  }), [rows]);

  const filteredRows = useMemo(() => {
    const productQuery = searchText(appliedFilter.reportProduct);
    const machineQuery = searchText(appliedFilter.reportMachine);
    return rows.filter(row => {
      if (productQuery) {
        const name = String(row.ProductName || "Produkt bez nazwy").trim();
        const label = row.EAN ? `${name} (${row.EAN})` : name;
        if (!searchText(label).includes(productQuery) && !searchText(row.EAN).includes(productQuery)) return false;
      }
      if (machineQuery) {
        const matches = (row.AllMachines || []).some(machine =>
          searchText(machine.Name).includes(machineQuery) || String(machine.MachineId || "") === machineQuery
        );
        if (!matches) return false;
      }
      return true;
    });
  }, [rows, appliedFilter]);

  const maxSpread = useMemo(() => Math.max(0, ...filteredRows.map(r => Number(r.Spread) || 0)), [filteredRows]);
  const sortBy = String(appliedFilter.sortBy || "none");
  const [sortField, sortDirection] = sortBy === "none"
    ? [null, null]
    : sortBy.split("|");

  const toggleSort = field => {
    const current = String(appliedFilterRef.current?.sortBy || "none");
    const [currentField, currentDirection] = current.split("|");
    let nextSortBy;
    if (currentField !== field) nextSortBy = `${field}|DESC`;
    else if (currentDirection === "DESC") nextSortBy = `${field}|ASC`;
    else nextSortBy = "none";
    loadData({ ...appliedFilterRef.current, sortBy: nextSortBy });
  };

  const arrow = field => sortField === field
    ? (sortDirection === "ASC" ? " ▲" : " ▼")
    : "";

  const ariaSort = field => sortField === field
    ? (sortDirection === "ASC" ? "ascending" : "descending")
    : "none";

  const handleSortKeyDown = (event, field) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleSort(field);
    }
  };

  return (
    <div className="m-2">
      <SearchFilter
        filterName="productPriceDeviationsReport"
        defaultFilter={DEFAULT_REPORT_FILTER}
        searchFilter={appliedFilter}
        search={loadData}
        reportOptions={reportOptions}
        forNow
      />

      {error && <div className="alert alert-danger mb-2">{error}</div>}
      {loading && <div className="text-primary small mb-2"><i className="fas fa-spinner fa-spin mr-1" /></div>}

      <div className="card overflow-auto">
        <div className="card-body p-0 custom-scroll border overflow-auto" style={{ maxHeight: Math.max(320, window.innerHeight - 260) }}>
          {!loading && !filteredRows.length ? (
            <div className="p-3"><NoResults /></div>
          ) : (
            <table className="table table-striped mb-0" style={{ minWidth: 1420 }}>
              <thead>
                <tr className="headers-stick-trxs">
                  <th
                    className="p-2"
                    style={{ minWidth: 230, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("ProductName")}
                    onKeyDown={event => handleSortKeyDown(event, "ProductName")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Nazwa produktu / EAN"
                    aria-sort={ariaSort("ProductName")}
                  >
                    Nazwa produktu / EAN{arrow("ProductName")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("MinPrice")}
                    onKeyDown={event => handleSortKeyDown(event, "MinPrice")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Cena min."
                    aria-sort={ariaSort("MinPrice")}
                  >
                    Cena min.{arrow("MinPrice")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ minWidth: 230, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("MinMachines")}
                    onKeyDown={event => handleSortKeyDown(event, "MinMachines")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Automaty z ceną min."
                    aria-sort={ariaSort("MinMachines")}
                  >
                    Automaty z ceną min.{arrow("MinMachines")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("LowerDeviation")}
                    onKeyDown={event => handleSortKeyDown(event, "LowerDeviation")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Odchylenie dolne"
                    aria-sort={ariaSort("LowerDeviation")}
                  >
                    Odchylenie dolne{arrow("LowerDeviation")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("AvgPrice")}
                    onKeyDown={event => handleSortKeyDown(event, "AvgPrice")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Średnia cena"
                    aria-sort={ariaSort("AvgPrice")}
                  >
                    Średnia cena{arrow("AvgPrice")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("UpperDeviation")}
                    onKeyDown={event => handleSortKeyDown(event, "UpperDeviation")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Odchylenie górne"
                    aria-sort={ariaSort("UpperDeviation")}
                  >
                    Odchylenie górne{arrow("UpperDeviation")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ minWidth: 230, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("MaxMachines")}
                    onKeyDown={event => handleSortKeyDown(event, "MaxMachines")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Automaty z ceną maks."
                    aria-sort={ariaSort("MaxMachines")}
                  >
                    Automaty z ceną maks.{arrow("MaxMachines")}
                  </th>
                  <th
                    className="p-2 text-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("MaxPrice")}
                    onKeyDown={event => handleSortKeyDown(event, "MaxPrice")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Cena maks."
                    aria-sort={ariaSort("MaxPrice")}
                  >
                    Cena maks.{arrow("MaxPrice")}
                  </th>
                  <th
                    className="p-2"
                    style={{ minWidth: 170, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("Spread")}
                    onKeyDown={event => handleSortKeyDown(event, "Spread")}
                    tabIndex={0}
                    role="button"
                    title="Sortuj: Rozpiętość cen"
                    aria-sort={ariaSort("Spread")}
                  >
                    Rozpiętość cen{arrow("Spread")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => {
                  const spread = Number(row.Spread || 0);
                  return (
                    <tr key={row.ProductKey}>
                      <td className="p-2" style={{ minWidth: 230 }}>
                        <strong>{row.ProductName || "Produkt bez nazwy"}</strong>
                        {row.EAN && <div className="text-muted small">EAN: {row.EAN}</div>}
                      </td>
                      <td className="p-2 text-center text-nowrap">{formatPrice(row.MinPrice)}</td>
                      <td className="p-2 text-center"><MachineNames key={`${row.ProductKey}-min`} id={`${row.ProductKey}-min`} machines={row.MinMachines} /></td>
                      <td className="p-2 text-center text-nowrap">
                        <strong className={Number(row.LowerDeviation) < 0 ? "text-danger" : "text-muted"}>
                          {formatDeviation(row.LowerDeviation)}
                        </strong>
                      </td>
                      <td className="p-2 text-center text-nowrap"><strong>{formatPrice(row.AvgPrice)}</strong></td>
                      <td className="p-2 text-center text-nowrap">
                        <strong className={Number(row.UpperDeviation) > 0 ? "text-success" : "text-muted"}>
                          {formatDeviation(row.UpperDeviation, true)}
                        </strong>
                      </td>
                      <td className="p-2 text-center"><MachineNames key={`${row.ProductKey}-max`} id={`${row.ProductKey}-max`} machines={row.MaxMachines} /></td>
                      <td className="p-2 text-center text-nowrap">{formatPrice(row.MaxPrice)}</td>
                      <td className="p-2">
                        <strong>{formatPrice(spread)}</strong>
                        <div className="progress mt-1" style={{ height: 6 }} title={`${Math.round(maxSpread ? (spread / maxSpread * 100) : 0)}% największej rozpiętości`}>
                          <div className="progress-bar bg-primary" style={{ width: `${maxSpread ? (spread / maxSpread * 100) : 0}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}